<template>

  <div class="affiliates-section">
    <TopBar>
      <template v-slot:left>
        <input v-model="searchInput" type="text" class="search-input" placeholder="Search..." />

        <button @click="createMissingInvoices" v-if="hasMissingInvoices" class="btn-sm btn-incoices">
          Calculate Invoices
        </button>
      </template>
      <template v-slot:right>
        <MonthSelector :minimum-date="new Date(2024, 0, 1)" />
      </template>
    </TopBar>

    <div class="card">
      <div class="affiliates-title">Affiliates</div>
      <div class="affiliates-table-wrapper">
        <table class="affiliates-table">
          <thead>
            <tr>
              <th class="sortable" @click="toggleSort('name')">
                Name <i class="fa" :class="sortIcon('name')"></i>
              </th>
              <th>Affiliation codes</th>
              <th>Attribution</th>
              <th>Model</th>
              <th>Commission</th>
              <th class="text-center sortable" @click="toggleSort('invoiceAmount')">
                Invoices <i class="fa" :class="sortIcon('invoiceAmount')"></i>
              </th>
              <th class="text-center">Invoice actions</th>
              <th>Actions</th>
            </tr>
          </thead>

          <tbody>
            <tr v-for="(affiliate, index) in affiliateInvoices" :key="'affiliate-' + index"
              :class="{ 'deactivated': affiliate.isDeactivated }">
              <td>{{ affiliate.name }}</td>
              <td style="max-width: 360px;">
                <AffiliationBubble
                  v-for="code in affiliate.affiliationCodes"
                  :key="code"
                  :affiliate="affiliate"
                  :code="code"
                />
              </td>
              <td>{{ affiliate.scheme.attributionModel }}</td>
              <td>{{ affiliationModelTitle(affiliate.scheme.affiliationModel) }}</td>
              <td>
                <span>
                  {{ affiliate.scheme.fixedCommission }}
                  {{ affiliate.scheme.affiliationModelSymbol }}
                </span>
                <br />
                <span>
                  {{ affiliate.scheme.feeCommission }}
                  {{ affiliate.scheme.affiliationModelSymbol }}
                </span>
              </td>
              <td>
                <span v-if="affiliate.invoice">
                  {{ affiliate.invoice.amount }}€ ({{ affiliate.invoice.month }}/{{ affiliate.invoice.year }})

                  <!-- TODO: re-instate this -->
                  <!-- <PerformanceInfoPopup v-if="index === idOfBanner && affiliate.invoice" @onHidePopUp="onHidePerformance"
                    :revenuesBreakdown="affiliate.invoice.revenuesBreakdown"></PerformanceInfoPopup> -->
                </span>
                <span v-else>
                  No invoice
                </span>
              </td>

              <td>

                <template v-if="!affiliate.invoice">
                  <button class="btn-invoice btn-sm"
                    @click="calculateAffiliateInvoice(affiliate)">Calculate Invoice</button>
                </template>
                <template v-else-if="affiliate.invoice.paid">
                  <span class="pill">Paid</span>
                </template>
                <template v-else>
                  <button class="btn-pay"
                    @click="markInvoiceAsPaid(affiliate.invoice.id)">Pay</button>
                </template>
              </td>

              <td class="actions-list">

                <button class="btn-edit btn-sm btn--full-width " type="button" v-if="showEditAffiliate"
                  @click="$router.push({ name: 'Affiliate', params: { code: affiliate.code } })">
                  <span>Edit <i class="fa fa-edit"></i></span>
                </button>

                <button class="btn-view btn-sm btn--full-width" @click="impersonateUser(affiliate)">
                  View as
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

  </div>
</template>

<script>

import TopBar from '../components/shared/TopBar.vue';
import { affiliationModelTitle } from '@/filters'
import { includes as _includes } from 'lodash-es';
import MonthSelector from '@/components/shared/MonthSelector.vue'
import PerformanceInfoPopup from '@/components/UIComponents/PerformanceInfoPopUp'
import { mapGetters } from 'vuex'
import ApiGateway from '../services/ApiGateway'

import AffiliationBubble from '@/components/shared/AffiliationBubble'

export default {
  components: {
    TopBar,
    MonthSelector,
    AffiliationBubble,
    PerformanceInfoPopup
  },
  data: () => ({
    affiliatesList: [],
    searchInput: '',
    sortBy: null,
    sortDirection: 'asc',
    idOfBanner: '',
    isWaiting: false
  }),
  watch: {
    // Executes instantly once component comes into view, and each time the date changes
    getActiveDates(previous, current) {
      this.getAffiliatesList();
    }
  },
  computed: {
    ...mapGetters({
      getActiveDates: 'datesModule/getActiveDates',
      getAllowedActions: 'authentication/getAllowedActions'
    }),
    affiliateInvoices() {
      const filtered = this.affiliatesList.filter(affiliate => this.searchForInputMatches(affiliate))

      if (!this.sortBy) {
        return filtered
      }

      const direction = this.sortDirection === 'asc' ? 1 : -1

      return [...filtered].sort((a, b) => {
        if (this.sortBy === 'name') {
          return direction * a.name.localeCompare(b.name)
        }

        const amountA = a.invoice ? a.invoice.amount : 0
        const amountB = b.invoice ? b.invoice.amount : 0
        return direction * (amountA - amountB)
      })
    },
    hasMissingInvoices() {
      return this.affiliatesList.some(affiliate => {
        return !affiliate.invoice
      })
    },
    showEditAffiliate() {
      return this.getAllowedActions.includes('EDIT_USER');
    },
  },
  methods: {
    affiliationModelTitle,
    toggleSort(field) {
      if (this.sortBy !== field) {
        this.sortBy = field
        this.sortDirection = 'asc'
        return
      }

      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc'
    },
    sortIcon(field) {
      if (this.sortBy !== field) {
        return 'fa-sort'
      }

      return this.sortDirection === 'asc' ? 'fa-sort-asc' : 'fa-sort-desc'
    },
    getAffiliatesList() {
      this.isWaiting = true;
      ApiGateway.affiliatesList().then(({ data }) => {
        this.affiliatesList = data;
      }).finally(() => this.isWaiting = false);
    },
    /**
     * *this method is called when the pay button for an affiliate is pressed,
     * *the invoice is set to paid in database and the paid flag is shown to the user
     */
    markInvoiceAsPaid(invoiceId) {
      
      ApiGateway.setPaid(invoiceId).then(() => {
        this.affiliatesList = this.affiliatesList.map(affiliate => {
          if ((affiliate.invoice) && (affiliate.invoice.id === invoiceId)) {
            return {
              ...affiliate,
              invoice: {
                ...affiliate.invoice,
                paid: true
              }
            }
          }

          return affiliate;
        })
      })
    },

    calculateAffiliateInvoice(affiliate) {
      ApiGateway.createInvoice(affiliate.id).then(({ data }) => {
        this.affiliatesList = data;
        this.$forceUpdate()
      })
        .catch(error => this.$store.dispatch('notificationsModule/errorNotify', error.toString()))
        .finally(() => {});
    },
    /**
     * * update the invoices that aren't calculated yet
     * * the invoices are calculated for the previous month of the date we have in the
     * * dater component
     */
    createMissingInvoices() {
      ApiGateway.createInvoices().then(({ data }) => {
        this.affiliatesList = data;
        this.$forceUpdate()
      })
    },
    /**
     * * we turn mastercode array into string and concat with name
     * * so that the includes method can check if the input exists as a part of the string
     * ! toUpperCase function is used to make search not case sensitive
     * @param affiliate
     */
    searchForInputMatches(affiliate) {
      let affiliationCodesString = affiliate.affiliationCodes.toString()
      let searchString = affiliationCodesString + affiliate.name

      return _includes(
        searchString.replace(/,/g, '').toUpperCase(),
        this.searchInput.toUpperCase()
      )
    },
    onShowPerformance(id) {
      this.idOfBanner = id
    },
    onHidePerformance() {
      this.idOfBanner = -1
    },
    impersonateUser(affiliate) {
      this.$store.dispatch('authentication/startImpersonation', affiliate);
      this.$router.push({ name: 'Overview', query: { impersonate: affiliate.id, impersonate_name: affiliate.name } });
    }
  },
}
</script>


<style scoped>
.affiliates-section {
  padding: 1rem 0 2rem 0;
}

.affiliates-title {
  font-size: 1.2rem;
  font-weight: 600;
  color: var(--gray-darker);
  margin-bottom: 1.1rem;
}


.search-input {
  width: 100%;
  max-width: 350px;
  padding: 0.5rem 1rem;
  border: 1px solid var(--gray-light);
  border-radius: 6px;
  font-size: 1rem;
  color: var(--gray-darker);
  background: var(--white);
}

.affiliates-table-wrapper {
  overflow-x: auto;
}

.affiliates-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.95rem;
  min-width: 600px;
  background: var(--white);
  border-radius: 10px;
  box-shadow: 0 2px 8px var(--gray-light);
}

.affiliates-table th {
  color: var(--gray);
  font-weight: 600;
  background: var(--gray-light);
  padding: 0.6rem 0.7rem;
  border-bottom: 1px solid var(--gray-d4);
  text-align: left;
  font-size: 0.95rem;
}

.affiliates-table th.sortable {
  cursor: pointer;
  user-select: none;
}

.affiliates-table th.sortable:hover {
  color: var(--gray-darker);
}

.affiliates-table th.sortable .fa {
  margin-left: 0.3rem;
  font-size: 0.85rem;
}

.affiliates-table td {
  color: var(--gray-darker);
  padding: 0.6rem 0.7rem;
  border-bottom: 1px solid var(--gray-light);
  vertical-align: middle;
  font-size: 0.95rem;
}

.deactivated {
  opacity: 1;
  background: #fff6f6;
}

.deactivated td {
  color: var(--gray);
  font-style: italic;
}

.actions-list {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  justify-content: flex-end;
  gap: 0.2rem;
}
</style>
